import { useState } from "react";

type Status = "idle" | "sending" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string>("");
  const [subject, setSubject] = useState<string>("製品について");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    // Honeypot check
    const honey = (formData.get("website_alt") as string | null) ?? "";
    if (honey.trim() !== "") {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    // Length validation
    const message = (formData.get("message") as string | null) ?? "";
    if (message.length > 500) {
      setStatus("error");
      setFormError("メッセージは500文字以内で入力してください。");
      return;
    }

    setStatus("sending");
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch(
        "https://readdy.ai/api/form/d95qo82h9roq6qfrc930",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: body.toString(),
        },
      );

      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
        setSubject("製品について");
      } else {
        setStatus("error");
        setFormError(
          serverMsg
            ? `送信に失敗しました：${serverMsg}`
            : "送信に失敗しました。時間をおいて再度お試しください。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("通信エラーが発生しました。ネットワーク環境をご確認ください。");
    }
  };

  const subjectOptions = ["製品について", "取扱店について", "取材・メディア", "その他"];

  return (
    <section
      id="contact"
      className="relative w-full py-24 md:py-32 bg-background-100 border-t border-background-200/60"
    >
      <div className="w-full px-6 md:px-10 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left side */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-[2px] bg-primary-500"></span>
              <span
                className="text-primary-500 text-xs tracking-[0.4em]"
                style={{ fontFamily: "var(--font-label)" }}
              >
                CONTACT
              </span>
            </div>
            <h4
              className="text-foreground-950 text-5xl md:text-6xl leading-[0.95] mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              <a href="#contact" className="hover:text-primary-500 transition-colors cursor-pointer">
                お問い合わせ<span className="text-primary-500">.</span>
              </a>
            </h4>
            <p className="text-foreground-700 text-base leading-relaxed max-w-md mb-10">
              製品仕様、取扱店、フィールドテスト等、
              お気軽にお問い合わせください。
            </p>

            <div className="space-y-5 border-t border-background-200/60 pt-8">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200 text-primary-500">
                  <i className="ri-map-pin-2-line text-lg"></i>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-foreground-9500 mb-1">
                    HEADQUARTERS
                  </div>
                  <div className="text-foreground-900 text-sm">
                    島根県松江市
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-foreground-9500 mb-1">
                    EMAIL
                  </div>
                  <div className="text-foreground-900 text-sm">hayabusaworks2024@gmail.com</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200 text-primary-500">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-foreground-9500 mb-1">
                    HOURS
                  </div>
                  <div className="text-foreground-900 text-sm">
                    お問い合わせメール受信後、４８時間以内にご連絡いたします
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              id="hayabusa-contact-form"
              data-readdy-form
              onSubmit={onSubmit}
              className="bg-background-50 border border-background-200/60 rounded-lg p-6 md:p-10"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-alt-input"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    お名前 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    メールアドレス <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="090-0000-0000"
                    className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    お問い合わせ種別
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {subjectOptions.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setSubject(s)}
                        className={`text-xs px-3 py-2 rounded-full border transition whitespace-nowrap cursor-pointer ${
                          subject === s
                            ? "bg-primary-500 border-primary-500 text-background-50"
                            : "bg-background-100 border-background-200/60 text-foreground-700 hover:border-primary-500/60"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" name="subject" value={subject} />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                  メッセージ <span className="text-primary-500">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition resize-none"
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="text-xs text-foreground-9500 leading-relaxed">
                  ※ ご入力いただいた情報は当社のプライバシーポリシーに従って取り扱います。
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 whitespace-nowrap cursor-pointer transition"
                >
                  <span
                    className="text-xs tracking-[0.3em]"
                    style={{ fontFamily: "var(--font-label)" }}
                  >
                    {status === "sending" ? "送信中..." : "送信する"}
                  </span>
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-primary-500 group-hover:rotate-45 transition-transform">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </button>
              </div>

              {status === "success" && (
                <div className="mt-6 rounded-md border border-primary-500/40 bg-primary-500/10 text-primary-500 text-sm px-4 py-3">
                  お問い合わせを受け付けました。担当より近日中にご連絡いたします。
                </div>
              )}
              {status === "error" && (
                <div className="mt-6 rounded-md border border-primary-700/40 bg-primary-950/40 text-primary-300 text-sm px-4 py-3">
                  {formError || "送信に失敗しました。時間をおいて再度お試しください。"}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}